import { useEffect, useState } from "react";
import AsideV2 from "../../../components/asidev2/AsideV2";
import {
  getAllBlogPosts,
  updateBlogById,
  deleteBlogById,
} from "../../../hooks/firebase";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { FaPlus, FaCheck, FaRegEdit, FaTrash } from "react-icons/fa";
import { LiaTimesSolid } from "react-icons/lia";
import HeaderV2 from "../../../components/adminV2/header/Header";


const storage = getStorage();

function BlogEditForm({ blog, id, onSave, onClose, onDelete }) {
  const [data, setData] = useState(blog);
  const [desktopImage, setDesktopImage] = useState(null);
  const [mobileImage, setMobileImage] = useState(null);
  const [desktopPreview, setDesktopPreview] = useState(blog.bannerDesktop || "");
  const [mobilePreview, setMobilePreview] = useState(blog.bannerMobile || "");

  const handleChange = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const generateSlug = (title) =>
    title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    handleChange("title", newTitle);
    handleChange("slug", generateSlug(newTitle));
  };

  const uploadImage = async (file, blogId, type) => {
    if (!file || !blogId) return null;
    const storageRef = ref(storage, `blogs/${blogId}/${type}-${uuidv4()}`);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const handleSaveClick = async () => {
    if (!data.title || !data.body) {
      alert("Título e corpo são obrigatórios");
      return;
    }

    let updated = { ...data };
    updated.isMain = updated.order === 1;

    if (desktopImage) {
      updated.bannerDesktop = await uploadImage(desktopImage, id, "desktop");
      setDesktopPreview(updated.bannerDesktop);
    }

    if (mobileImage) {
      updated.bannerMobile = await uploadImage(mobileImage, id, "mobile");
      setMobilePreview(updated.bannerMobile);
    }

    onSave(id, updated);
  };

  return (
    <div className="p-3 bg-light border rounded mt-2">
      <div className="mb-3 row">
        <label className="col-sm-2 col-form-label fw-semibold">Título</label>
        <div className="col-sm-10">
          <input
            type="text"
            className="form-control"
            value={data.title}
            onChange={handleTitleChange}
          />
        </div>
      </div>

      <div className="mb-3 row">
        <label className="col-sm-2 col-form-label fw-semibold">Autor</label>
        <div className="col-sm-10">
          <input
            type="text"
            className="form-control"
            value={data.author || ""}
            onChange={(e) => handleChange("author", e.target.value)}
          />
        </div>
      </div>

      <div className="mb-3 row">
        <label className="col-sm-2 col-form-label fw-semibold">Tema</label>
        <div className="col-sm-10">
          <input
            type="text"
            className="form-control"
            value={data.theme || ""}
            onChange={(e) => handleChange("theme", e.target.value)}
          />
        </div>
      </div>

      <div className="mb-3 row">
        <label className="col-sm-2 col-form-label fw-semibold">Ordem</label>
        <div className="col-sm-10">
          <input
            type="number"
            className="form-control"
            value={data.order || ""}
            onChange={(e) =>
              handleChange("order", Number(e.target.value) || 0)
            }
          />
        </div>
      </div>

      <div className="mb-3 row">
        <label className="col-sm-2 col-form-label fw-semibold">Slug</label>
        <div className="col-sm-10">
          <input type="text" className="form-control" value={data.slug || ""} disabled />
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold">Conteúdo</label>
        <textarea
          className="form-control"
          rows={6}
          value={data.body || ""}
          onChange={(e) => handleChange("body", e.target.value)}
        />
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold">Banner Desktop</label>
        <input
          type="file"
          className="form-control mb-2"
          onChange={(e) => setDesktopImage(e.target.files[0])}
        />
        {desktopPreview && (
          <img
            src={desktopPreview}
            alt="Desktop Banner Preview"
            className="img-fluid rounded"
          />
        )}
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold">Banner Mobile</label>
        <input
          type="file"
          className="form-control mb-2"
          onChange={(e) => setMobileImage(e.target.files[0])}
        />
        {mobilePreview && (
          <img
            src={mobilePreview}
            alt="Mobile Banner Preview"
            className="img-fluid rounded"
          />
        )}
      </div>

      <div className="d-flex justify-content-end gap-2">
        <button className="btn btn-danger" onClick={() => onDelete(data)}>
          <FaTrash className="me-1" />
          Excluir
        </button>
        <button className="btn btn-success" onClick={handleSaveClick}>
          <FaCheck className="me-1" />
          Salvar
        </button>
        <button className="btn btn-secondary" onClick={onClose}>
          <LiaTimesSolid className="me-1" />
          Cancelar
        </button>
      </div>
    </div>
  );
}

export default function BlogsV2() {
  const [blogs, setBlogs] = useState({});
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    async function load() {
      const result = await getAllBlogPosts();
      setBlogs(result);
    }
    load();
  }, []);

  const handleSaveBlog = async (id, updatedBlog) => {
    const blogId = updatedBlog.id || id;
    setBlogs((prev) => ({ ...prev, [blogId]: updatedBlog }));

    await updateBlogById(blogId, {
      ...updatedBlog,
      body: JSON.stringify(updatedBlog.body),
    });

    const newData = await getAllBlogPosts();
    setBlogs(newData);
    setEditingId(null);
  };

  const handleDeleteBlog = async (toDelete) => {
    if (!toDelete?.id) return;
    await deleteBlogById(toDelete.id);
    const newData = await getAllBlogPosts();
    setBlogs(newData);
    setEditingId(null);
  };

  const handleAddBlog = () => {
    const newId = uuidv4();
    setBlogs((prev) => ({
      ...prev,
      [newId]: {
        title: "",
        author: "",
        theme: "",
        slug: "",
        body: "",
        bannerDesktop: "",
        bannerMobile: "",
        order: 0,
      },
    }));
    setEditingId(newId);
  };

  return (
    <div>
      <AsideV2 />
      <main className="main-wrapper p-4">
        <HeaderV2 />
        <div className="d-flex align-items-center mb-4">
          <h1 className="me-auto">Blogs</h1>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAddBlog}
          >
            <FaPlus className="me-2" />
            Novo Blog
          </button>
        </div>

        <div className="accordion" id="blogsAccordion">
          {Object.entries(blogs).map(([id, blog]) => (
            <div className="accordion-item" key={id}>
              <h2 className="accordion-header" id={`heading-${id}`}>
                <button
                  className={`accordion-button ${
                    editingId === id ? "" : "collapsed"
                  }`}
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target={`#collapse-${id}`}
                  aria-expanded={editingId === id}
                  aria-controls={`collapse-${id}`}
                  onClick={() =>
                    setEditingId(editingId === id ? null : id)
                  }
                >
                  <span className="me-3">{blog.order}</span>
                  <span className="me-auto">
                    {blog.title || "Novo Blog"}
                  </span>
                  {editingId !== id && <FaRegEdit />}
                </button>
              </h2>
              <div
                id={`collapse-${id}`}
                className={`accordion-collapse collapse ${
                  editingId === id ? "show" : ""
                }`}
                aria-labelledby={`heading-${id}`}
                data-bs-parent="#blogsAccordion"
              >
                <div className="accordion-body">
                  <BlogEditForm
                    blog={blog}
                    id={id}
                    onSave={handleSaveBlog}
                    onClose={() => setEditingId(null)}
                    onDelete={handleDeleteBlog}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
